import gql from 'graphql-tag';

import { apolloClient } from '@/apollo/apollo';
import * as appColors from '@/functions/appColors';
import * as actions from '@/constants/actions';

export default {
    mounted() {
        this.initialize();
    },
    data() {
        return {
            queryDone: false,
            queryData: {},
            chemistryData: {},
            dataQuery: gql`query stationGwLevel($id: Int!, $type: Int!) {
                getStation(id: $id) {
                    name
                    nid
                    net
                    yr
                    ty
                    licence_link
                    aquifer_number,
                    aquifer_class,
                    aquifer_type,
                    description
                    hydrograph {
                        historical
                        current
                    }
                    monthly_mean_flow(type: $type) {
                        current
                        yearly
                    }
                }
            }`,
            properties: [],
            hydrographDatasets: null,
            tableData: null,
        };
    },
    methods: {
        /**
         * handle initial data fetching and potentially station changes
         */
        async initialize() {
            // reset local vars
            this.queryDone = false;
            this.queryData = {};

            this.hydrographDatasets = null;

            if (!this.station) return;

            // fetch data
            await this.getData();
            this.formatTableData();
            this.buildProps();
        },
        /**
         * query the API and fetch station data
         */
        async getData() {
            if (!this.station) return;
            try {
                this.$store.dispatch(actions.ADD_LOADING, 'load station data');
                const { data } = await apolloClient.query({
                    query: this.dataQuery,
                    variables: {
                        id: this.station.id,
                        type: 3,
                    },
                });

                // populate variables
                this.buildDatasets(data.getStation);
                this.queryData = data.getStation;

                this.queryDone = true;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving station data');
            }
            this.$store.dispatch(actions.REMOVE_LOADING, 'load station data');
        },
        /**
         * build an array of properties to display
         */
        buildProps() {
            const data = this.queryData;
            const properties = [];

            properties.push({
                title: 'Description',
                value: data.description,
            });

            properties.push({
                title: 'Date Range',
                value: `${this.years[0]}-${this.years[1]}`,
            });

            if (data.aquifer_number && data.aquifer_type) {
                properties.push({
                    title: 'Aquifer',
                    value: `${data.aquifer_number} - ${data.aquifer_type}`,
                });
            }

            properties.push({
                title: 'Aquifer Class',
                value: data.aquifer_class,
            });

            this.properties = properties;
        },
        /**
         *
         * @return {{headers: *[], dataMap: {avg: {}, max: {}, min: {}}}}
         */
        formatTableData() {
            const { yearly, current } = this.queryData.monthly_mean_flow;
            const headers = [
                { text: 'Year' },
                { text: 'Jan' },
                { text: 'Feb' },
                { text: 'Mar' },
                { text: 'Apr' },
                { text: 'May' },
                { text: 'Jun' },
                { text: 'Jul' },
                { text: 'Aug' },
                { text: 'Sep' },
                { text: 'Oct' },
                { text: 'Nov' },
                { text: 'Dec' },
            ];

            const items = [];

            let currentMean = ['Mean'].concat(current.map(e => e.avg));
            currentMean = currentMean.map(e => ({ text: e }));
            let currentMax = ['Maximum'].concat(current.map(e => e.max));
            currentMax = currentMax.map(e => ({ text: e }));
            let currentMin = ['Minimum'].concat(current.map(e => e.min));
            currentMin = currentMin.map(e => ({ text: e }));

            items.push(currentMean, currentMax, currentMin);

            const currentMap = {
                avg: {
                    year: 'Mean',
                },
                max: {
                    year: 'Maximum',
                },
                min: {
                    year: 'Minimum',
                },
            };

            current.forEach((item) => {
                currentMap.avg[item.m] = item.avg;
                currentMap.max[item.m] = item.max;
                currentMap.min[item.m] = item.min;
            });

            const yearlyMap = {};
            yearly.forEach((item) => {
                const { year, m, v } = item;
                if (yearlyMap[year] === undefined) {
                    yearlyMap[year] = [year];
                }
                yearlyMap[year][m] = v;
            });

            // Set data
            let yearlyItems = Object.values(yearlyMap);
            yearlyItems = yearlyItems.map(year => year.map(e => ({ text: e })));

            // console.log('table', headers, items, yearlyItems);
            this.tableData = [
                headers,
                ...items,
                ...yearlyItems,
            ];
        },
        buildDatasets(data) {
            this.hydrographDatasets = [
                {
                    label: 'Historical Mean',
                    data: data.hydrograph.historical.map(d => d.a),
                    borderColor: appColors.sparklineColor2,
                },
                {
                    label: 'Historical Max',
                    data: data.hydrograph.historical.map(d => d.max),
                    borderColor: appColors.sparklineColor1,
                },
                {
                    label: 'Historical Min',
                    data: data.hydrograph.historical.map(d => d.min),
                    borderColor: appColors.sparklineColor1,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
            ];
        },
    },
    computed: {
        station() {
            return this.$store.state.map.selectedStation;
        },
        name() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.name;
        },
        networkUrl() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.licence_link;
        },
        network() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.net;
        },
        networkId() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.nid;
        },
        type() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.ty;
        },
        years() {
            if (!this.queryDone) {
                return [];
            }
            return this.queryData.yr;
        },
        numYears() {
            if (this.years.length === 2) {
                const years = this.years[1] - this.years[0];
                return years + 1;
            }
            return 0;
        },
    },
    watch: {
        async station() {
            // re-initialize component when a new station is selected
            this.initialize();
        },
    },
};



// WEBPACK FOOTER //
// ./src/mixins/groundLevelData.js